import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../config';
import {
  addBranding,
  deleteBranding,
  editBranding,
  getBranding,
  getMyBrands,
} from './methods';
import { EditBrandingRequest } from './types';

const useAddBrandingMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: addBrandingMutation,
  } = useMutation({
    mutationFn: addBranding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.brandingKey] });
    },
  });

  return { addBrandingMutation, isError, isPending, isSuccess };
};

const useEditBrandingMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: editBrandingMutation,
  } = useMutation({
    mutationFn: ({
      id,
      formData,
    }: {
      id: number;
      formData: EditBrandingRequest;
    }) => editBranding(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.brandingKey] });
    },
  });

  return { editBrandingMutation, isError, isPending, isSuccess };
};

const useDeleteBrandingMutation = () => {
  const queryClient = useQueryClient();

  const {
    isPending,
    isSuccess,
    isError,
    mutateAsync: deleteBrandingMutation,
  } = useMutation({
    mutationFn: deleteBranding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.brandingKey] });
    },
  });

  return { deleteBrandingMutation, isError, isPending, isSuccess };
};

const useGetBrandingQuery = (id: number) => {
  const { isPending, isSuccess, isError, data } = useQuery({
    enabled: !!id,
    queryFn: () => getBranding(id),
    queryKey: [queryKeys.brandingKey, id],
  });

  return { data, isError, isPending, isSuccess };
};

const useGetMyBrandsQuery = () => {
  const { isLoading, isSuccess, isError, data } = useQuery({
    queryFn: getMyBrands,
    queryKey: [queryKeys.brandingKey],
  });

  return { data, isError, isLoading, isSuccess };
};

export {
  useAddBrandingMutation,
  useDeleteBrandingMutation,
  useEditBrandingMutation,
  useGetBrandingQuery,
  useGetMyBrandsQuery,
};
